/**
 * `fetch` avec délai d'expiration pour le client Supabase.
 *
 * Sans délai, une requête qui ne répond jamais (onglet mobile repris après une
 * mise en veille, réseau qui décroche) laisse la promesse en attente pour
 * toujours : React Query reste en chargement, sans erreur ni nouvel essai
 * (page SKILLCARD bloquée sur son squelette). Pire, un rafraîchissement de
 * jeton bloqué retient le verrou d'auth et gèle TOUTES les requêtes suivantes.
 *
 * Seules les API courtes sont bornées (PostgREST/RPC et Auth) : les edge
 * functions (appels LLM jusqu'à 150 s) et le Storage (envois de fichiers)
 * gardent le comportement natif.
 */
export const SUPABASE_REQUEST_TIMEOUT_MS = 20_000;

const TIMED_PATHS = ['/rest/v1/', '/auth/v1/'];

export function shouldTimeoutRequest(url: string): boolean {
  return TIMED_PATHS.some((p) => url.includes(p));
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

export function createFetchWithTimeout(
  timeoutMs: number = SUPABASE_REQUEST_TIMEOUT_MS,
  baseFetch: typeof fetch = (...args) => fetch(...args),
): typeof fetch {
  return (input, init) => {
    if (!shouldTimeoutRequest(requestUrl(input))) return baseFetch(input, init);

    const controller = new AbortController();
    const outer = init?.signal;
    const onAbort = () => controller.abort(outer?.reason);
    if (outer) {
      if (outer.aborted) controller.abort(outer.reason);
      else outer.addEventListener('abort', onAbort, { once: true });
    }
    const timer = setTimeout(
      () => controller.abort(new DOMException('La requête a expiré.', 'TimeoutError')),
      timeoutMs,
    );
    return baseFetch(input, { ...init, signal: controller.signal }).finally(() => {
      clearTimeout(timer);
      outer?.removeEventListener('abort', onAbort);
    });
  };
}
